import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    Box, AppBar, Toolbar, Typography, Stack, Chip, IconButton, Tooltip, useMediaQuery,
} from "@mui/material";
import Compressor from "compressorjs";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import LogoutIcon from "@mui/icons-material/Logout";
import SettingsIcon from "@mui/icons-material/Settings";
import livechatApi from "./livechatApi";
import { connectLivechatSocket, disconnectLivechatSocket } from "./livechatSocket";
import ChannelRail from "./ChannelRail";
import ConversationList from "./ConversationList";
import ChatPanel from "./ChatPanel";
import CustomerContextPanel from "./CustomerContextPanel";
import SetLabelDialog from "./SetLabelDialog";
import GenerateLinkDialog from "./GenerateLinkDialog";
import LiveChatSettingsDialog from "./LiveChatSettingsDialog";
import { playNotificationSound } from "./livechatSound";
import { PASTEL } from "./livechatTheme";
import Global from "../../customlib/Global";;

const LIVECHAT_MESSAGES_PAGE_SIZE = 50;

function compressImage(file) {
    return new Promise((resolve, reject) => {
        new Compressor(file, {
            quality: 0.8,
            maxWidth: 1600,
            maxHeight: 1600,
            success: resolve,
            error: reject,
        });
    });
}

// Halaman baru DEV-1049 — TIDAK memanggil Global.getAppProfile() sama sekali (App Profile
// tetap wajib dipilih saat login, tapi nilainya diabaikan total oleh modul ini). Auth ke
// lotus-be-express reuse Global.get().be_express_token yang sudah ada (lihat livechatApi.js).
export default function CrmLiveChat() {
    const isMobile = useMediaQuery("(max-width:900px)");
    const [brands, setBrands] = useState([]);
    const [activeBrand, setActiveBrand] = useState("all");
    const [filter, setFilter] = useState("active");
    const [search, setSearch] = useState("");
    const [sessions, setSessions] = useState([]);
    const [selectedSession, setSelectedSession] = useState(null);
    const [messages, setMessages] = useState([]);
    const [messagesHasMore, setMessagesHasMore] = useState(false);
    const [loadingOlderMessages, setLoadingOlderMessages] = useState(false);
    const nextMessagesPageRef = useRef(2);
    const [labelDialogOpen, setLabelDialogOpen] = useState(false);
    const [generatedLink, setGeneratedLink] = useState(null);
    const [unreadTotal, setUnreadTotal] = useState(0);
    const [channelUnread, setChannelUnread] = useState({});
    const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
    const isActiveRef = useRef(true);

    const socket = useMemo(() => connectLivechatSocket(), []);

    useEffect(() => {
        // Suara notifikasi dibuat lebih keras (loud) saat tab sedang tidak difokus agent —
        // visible saja tidak cukup (tab masih "visible" saat alt-tab ke app lain), makanya
        // digabung dengan window focus/blur. Mirror dari livechat-customer-fe/ChatScreen.jsx.
        const updateActivityState = () => {
            isActiveRef.current = document.visibilityState === "visible" && document.hasFocus();
        };
        updateActivityState();
        document.addEventListener("visibilitychange", updateActivityState);
        window.addEventListener("focus", updateActivityState);
        window.addEventListener("blur", updateActivityState);
        return () => {
            document.removeEventListener("visibilitychange", updateActivityState);
            window.removeEventListener("focus", updateActivityState);
            window.removeEventListener("blur", updateActivityState);
        };
    }, []);

    const loadInbox = useCallback(async () => {
        const params = {
            brand: activeBrand !== "all" ? activeBrand : undefined,
            status: filter === "closed" ? "closed" : filter === "active" ? "active" : undefined,
            unreadOnly: filter === "unread" ? "true" : undefined,
            search: search || undefined,
            limit: 50,
        };
        const { data } = await livechatApi.listInbox(params);
        setSessions(data.data || []);
        setUnreadTotal((data.data || []).filter((s) => s.unreadByAgent).length);
    }, [activeBrand, filter, search]);

    const loadBrands = useCallback(() => {
        livechatApi.listBrands().then(({ data }) => setBrands(data.data?.brands || []));
    }, []);

    // Unread count per-kanal untuk ChannelRail — independen dari filter/search/activeBrand
    // yang sedang aktif di ConversationList (rail harus tetap menampilkan total unread SEMUA
    // kanal, bukan cuma kanal yang lagi dipilih). limit:1 karena hanya `additional_data.total`
    // (bukan daftar sesi-nya) yang dipakai di sini.
    const loadChannelUnread = useCallback(async () => {
        const targets = [{ key: "all", brand: undefined }, ...brands.map((b) => ({ key: b.brand, brand: b.brand }))];
        const entries = await Promise.all(
            targets.map(({ key, brand }) =>
                livechatApi
                    .listInbox({ brand, unreadOnly: "true", limit: 1 })
                    .then(({ data }) => [key, data.additional_data?.total || 0])
                    .catch(() => [key, 0])
            )
        );
        setChannelUnread(Object.fromEntries(entries));
    }, [brands]);

    useEffect(() => {
        loadBrands();
    }, [loadBrands]);

    useEffect(() => {
        loadInbox();
    }, [loadInbox]);

    useEffect(() => {
        loadChannelUnread();
    }, [loadChannelUnread]);

    useEffect(() => {
        document.title = unreadTotal > 0 ? `(${unreadTotal}) Live Chat` : "Live Chat";
    }, [unreadTotal]);

    useEffect(() => {
        // Sesi baru (inbox:session:new) selalu berarti pesan pertama dari customer yang belum
        // dibaca (unreadByAgent selalu true saat sesi dibuat, lihat livechat.session.service.ts).
        // Untuk inbox:session:updated, backend TIDAK menyertakan `sender` di payload — tapi
        // unreadByAgent hanya bernilai true kalau update ini berasal dari pesan customer yang
        // belum ditandai dibaca (pesan agent sendiri tidak mengubah unreadByAgent, lihat
        // livechat.message.service.ts#createMessage), jadi flag ini sudah cukup untuk membedakan
        // "customer baru menulis" dari "echo pesan balasan agent sendiri" tanpa perlu field baru.
        const onNewSession = () => {
            loadInbox();
            loadChannelUnread();
            playNotificationSound(!isActiveRef.current);
        };
        const onSessionUpdated = (payload) => {
            loadInbox();
            loadChannelUnread();
            if (payload?.unreadByAgent) playNotificationSound(!isActiveRef.current);
        };
        socket.on("inbox:session:new", onNewSession);
        socket.on("inbox:session:updated", onSessionUpdated);
        return () => {
            socket.off("inbox:session:new", onNewSession);
            socket.off("inbox:session:updated", onSessionUpdated);
        };
    }, [socket, loadInbox, loadChannelUnread]);

    useEffect(() => {
        return () => disconnectLivechatSocket();
    }, []);

    // Dipakai baik oleh optimistic-update di handleSendMessage/handleSendPhoto maupun oleh
    // listener socket "conversation:message:new" — dedupe by _id supaya pesan yang sama tidak
    // dobel dirender kalau backend suatu saat juga meng-echo pesan milik sendiri lewat socket.
    const appendMessage = useCallback((msg) => {
        if (!msg) return;
        setMessages((prev) => (msg._id && prev.some((m) => m._id === msg._id)) ? prev : [...prev, msg]);
    }, []);

    const handleSelectSession = useCallback(async (session) => {
        setSelectedSession(session);
        const { data } = await livechatApi.getSessionDetail(session._id, { page: 1, limit: LIVECHAT_MESSAGES_PAGE_SIZE });
        setMessages(data.data?.messages || []);
        setMessagesHasMore(!!data.data?.hasMoreMessages);
        nextMessagesPageRef.current = 2;
        await livechatApi.markRead(session._id);
        loadInbox();
        loadChannelUnread();

        socket.emit("conversation:join", { brand: session.brand, phone: session.customerPhone });
    }, [socket, loadInbox, loadChannelUnread]);

    // Dipanggil ChatPanel saat pengguna scroll mentok ke atas daftar pesan — memuat halaman
    // pesan lebih LAMA (page 2, 3, ...) dan menaruhnya di AWAL array (bukan replace total seperti
    // handleSelectSession), dedupe by _id jaga-jaga kalau ada overlap antar halaman.
    const handleLoadOlderMessages = useCallback(async () => {
        if (!selectedSession || loadingOlderMessages || !messagesHasMore) return;
        setLoadingOlderMessages(true);
        try {
            const page = nextMessagesPageRef.current;
            const { data } = await livechatApi.getSessionDetail(selectedSession._id, { page, limit: LIVECHAT_MESSAGES_PAGE_SIZE });
            const older = data.data?.messages || [];
            setMessages((prev) => {
                const existingIds = new Set(prev.map((m) => m._id));
                const merged = older.filter((m) => !existingIds.has(m._id));
                return [...merged, ...prev];
            });
            setMessagesHasMore(!!data.data?.hasMoreMessages);
            nextMessagesPageRef.current = page + 1;
        } catch (error) {
            console.error("Error in CrmLiveChat.handleLoadOlderMessages:", error);
        } finally {
            setLoadingOlderMessages(false);
        }
    }, [selectedSession, loadingOlderMessages, messagesHasMore]);

    useEffect(() => {
        if (!selectedSession) return;

        const onNewMessage = (msg) => {
            if (msg.sessionId?.toString() !== selectedSession._id?.toString()) return;
            // Pesan milik agent sendiri sudah ditampilkan lewat jalur optimistic di
            // attemptSendMessage/attemptSendPhoto (bubble "sending" -> di-update jadi pesan asli
            // dari response REST). Backend ternyata tetap meng-echo event ini balik ke room
            // (termasuk ke socket pengirimnya), dan karena bubble optimistic masih ber-_id
            // sementara (tempId) saat echo ini tiba, dedupe by _id di appendMessage tidak akan
            // match -> jadi 2 bubble untuk pesan yang sama. Abaikan echo utk sender "agent".
            if (msg.sender === "agent") return;
            appendMessage(msg);
            // Agent sedang membuka percakapan ini saat pesan baru dari customer masuk -> tandai
            // dibaca langsung, jangan tunggu pilih ulang sesi (markRead sebelumnya cuma dipanggil
            // sekali saat handleSelectSession, jadi pesan yang masuk setelah itu tidak pernah
            // ditandai dibaca selama sesi masih terbuka).
            livechatApi.markRead(selectedSession._id).catch(() => {});
        };
        // Customer membuka kembali percakapan -> tandai pesan milik agent sebagai dibaca (✓✓),
        // tanpa perlu reload/refetch (lihat livechat.message.service.ts#markRead di backend).
        const onReadAck = ({ by, readAt }) => {
            if (by !== "customer") return;
            setMessages((prev) => prev.map((m) => (
                m.sender === "agent" && !m.readByCustomerAt ? { ...m, readByCustomerAt: readAt } : m
            )));
        };

        socket.on("conversation:message:new", onNewMessage);
        socket.on("conversation:read:ack", onReadAck);
        return () => {
            socket.off("conversation:message:new", onNewMessage);
            socket.off("conversation:read:ack", onReadAck);
            if (selectedSession) {
                socket.emit("conversation:leave", { brand: selectedSession.brand, phone: selectedSession.customerPhone });
            }
        };
    }, [socket, selectedSession, appendMessage]);

    // Kirim pesan teks dibuat optimistic (bubble "sending" langsung tampil di daftar, di-update
    // jadi bubble asli dari response REST kalau sukses, atau "failed" + tombol retry kalau gagal)
    // — sebelumnya kegagalan di sini silent total: input sudah keburu dikosongkan di ChatPanel
    // dan tidak ada bubble apa pun yang menandai pesannya hilang.
    const attemptSendMessage = useCallback(async (tempId, sessionId, text) => {
        setMessages((prev) => prev.map((m) => (m._id === tempId ? { ...m, deliveryStatus: "sending" } : m)));
        try {
            const { data } = await livechatApi.sendMessage(sessionId, { text });
            const sent = data.data?.message || data.data;
            setMessages((prev) => prev.map((m) => (m._id === tempId ? { ...sent, deliveryStatus: "sent" } : m)));
        } catch (error) {
            console.error("Error in CrmLiveChat.attemptSendMessage:", error);
            setMessages((prev) => prev.map((m) => (m._id === tempId ? { ...m, deliveryStatus: "failed" } : m)));
        }
    }, []);

    const handleSendMessage = useCallback((text) => {
        if (!selectedSession) return;
        const sessionId = selectedSession._id;
        const tempId = `temp-${Date.now()}`;
        setMessages((prev) => [...prev, {
            _id: tempId, sender: "agent", text, deliveryStatus: "sending", created_at: new Date().toISOString(),
        }]);
        attemptSendMessage(tempId, sessionId, text);
    }, [selectedSession, attemptSendMessage]);

    // Kompresi dipindah ke sini (dari ChatPanel) supaya kegagalannya (bukan cuma upload/kirim)
    // ikut tertangkap oleh bubble optimistic yang sama, konsisten dengan pola
    // livechat-customer-fe/ChatScreen.jsx#handleSendPhoto (bubble "sending" dibuat DULU, baru
    // kompres+upload+kirim dibungkus try/catch — bukan kompres dulu baru bikin bubble).
    const attemptSendPhoto = useCallback(async (tempId, sessionId, file, caption, previewUrl) => {
        setMessages((prev) => prev.map((m) => (m._id === tempId ? { ...m, deliveryStatus: "sending" } : m)));
        try {
            const compressed = await compressImage(file);
            const { data: uploadRes } = await livechatApi.uploadPhoto(compressed);
            const url = uploadRes.data?.url;
            const { data } = await livechatApi.sendMessage(sessionId, { text: caption || undefined, attachmentUrl: url });
            const sent = data.data?.message || data.data;
            setMessages((prev) => prev.map((m) => (m._id === tempId ? { ...sent, deliveryStatus: "sent" } : m)));
            URL.revokeObjectURL(previewUrl);
        } catch (error) {
            console.error("Error in CrmLiveChat.attemptSendPhoto:", error);
            setMessages((prev) => prev.map((m) => (m._id === tempId ? { ...m, deliveryStatus: "failed" } : m)));
        }
    }, []);

    const handleSendPhoto = useCallback((file, caption, previewUrl) => {
        if (!selectedSession) return;
        const sessionId = selectedSession._id;
        const tempId = `temp-${Date.now()}`;
        setMessages((prev) => [...prev, {
            _id: tempId, sender: "agent", text: caption || undefined, attachmentUrl: previewUrl,
            deliveryStatus: "sending", created_at: new Date().toISOString(),
            // disimpan di bubble sendiri (bukan closure) supaya retry tetap bisa dipakai walau
            // komponen re-render/unmount di antara percobaan pertama & retry.
            _retryFile: file, _retryCaption: caption,
        }]);
        attemptSendPhoto(tempId, sessionId, file, caption, previewUrl);
    }, [selectedSession, attemptSendPhoto]);

    // Dipanggil dari tombol retry di bubble "Gagal terkirim" (ChatPanel). Sengaja pakai
    // selectedSession._id SAAT RETRY diklik (bukan menyimpan sessionId pesan gagal) — pesan
    // gagal cuma bisa terlihat & di-retry selama percakapan yang sama masih dibuka, karena
    // `messages` di-replace total tiap kali pindah sesi (lihat handleSelectSession).
    const handleRetry = useCallback((msg) => {
        if (msg.deliveryStatus !== "failed" || !selectedSession) return;
        if (msg._retryFile) {
            attemptSendPhoto(msg._id, selectedSession._id, msg._retryFile, msg._retryCaption, msg.attachmentUrl);
        } else {
            attemptSendMessage(msg._id, selectedSession._id, msg.text);
        }
    }, [selectedSession, attemptSendMessage, attemptSendPhoto]);

    const handleCloseSession = async () => {
        const { data } = await livechatApi.closeSession(selectedSession._id);
        setSelectedSession(data.data);
        loadInbox();
    };

    const handleGenerateLink = async () => {
        const { data } = await livechatApi.generateLink(selectedSession._id);
        setGeneratedLink(data.data?.url || "");
    };

    const handleLogout = () => {
        disconnectLivechatSocket();
        localStorage.removeItem("auth");
        localStorage.removeItem("selectedAppProfile");
        window.location.href = "/";
    };

    return (
        <Box sx={{ display: "flex", flexDirection: "column", height: "100vh" }}>
            <AppBar position="static" color="inherit" elevation={0} sx={{ borderBottom: `1px solid ${PASTEL.border}`, backgroundColor: PASTEL.surface }}>
                <Toolbar variant="dense" sx={{ minHeight: { xs: 56, sm: 64 }, px: { xs: 1.5, sm: 3 }, gap: { xs: 1, sm: 2.5 } }}>
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexShrink: 0 }}>
                        <Box
                            sx={{
                                width: 36, height: 36, borderRadius: 2, flexShrink: 0,
                                display: { xs: "none", sm: "flex" }, alignItems: "center", justifyContent: "center",
                                backgroundColor: PASTEL.primarySoft,
                            }}
                        >
                            <ChatBubbleOutlineIcon sx={{ color: PASTEL.primary, fontSize: 20 }} />
                        </Box>
                        <Box>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <Typography variant="subtitle1" fontWeight={700} lineHeight={1.2}>Live Chat</Typography>
                                {unreadTotal > 0 && (
                                    <Chip
                                        size="small"
                                        color="error"
                                        label={unreadTotal}
                                        sx={{ height: 18, fontSize: 10, fontWeight: 700 }}
                                    />
                                )}
                            </Stack>
                            <Typography variant="caption" color="text.secondary" sx={{ display: { xs: "none", sm: "block" } }}>
                                {sessions.length} percakapan
                            </Typography>
                        </Box>
                    </Stack>

                    <Box sx={{ flex: 1 }} />

                    {
                        Global.get().admin && (
                            <Tooltip title="Settings">
                                <IconButton onClick={() => setSettingsDialogOpen(true)} size="small" sx={{ color: "text.secondary" }}>
                                    <SettingsIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )
                    }

                    <Tooltip title="Logout">
                        <IconButton onClick={handleLogout} size="small" sx={{ color: "text.secondary" }}>
                            <LogoutIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Toolbar>
            </AppBar>

            <Box sx={{ flex: 1, display: "flex", overflow: "hidden" }}>
                {(!isMobile || !selectedSession) && (
                    <ChannelRail
                        brands={brands}
                        activeBrand={activeBrand}
                        onSelect={setActiveBrand}
                        unreadByBrand={channelUnread}
                    />
                )}

                {(!isMobile || !selectedSession) && (
                    <ConversationList
                        sessions={sessions}
                        selectedSessionId={selectedSession?._id}
                        onSelect={handleSelectSession}
                        filter={filter}
                        onFilterChange={setFilter}
                        search={search}
                        onSearchChange={setSearch}
                    />
                )}

                {(!isMobile || selectedSession) && (
                    <ChatPanel
                        session={selectedSession}
                        messages={messages}
                        hasMoreMessages={messagesHasMore}
                        loadingOlderMessages={loadingOlderMessages}
                        onLoadOlderMessages={handleLoadOlderMessages}
                        onSendMessage={handleSendMessage}
                        onSendPhoto={handleSendPhoto}
                        onRetryMessage={handleRetry}
                        onCloseSession={handleCloseSession}
                        onOpenLabelDialog={() => setLabelDialogOpen(true)}
                        isMobile={isMobile}
                        onBack={() => setSelectedSession(null)}
                    />
                )}

                {selectedSession && !isMobile && (
                    <CustomerContextPanel session={selectedSession} onGenerateLink={handleGenerateLink} />
                )}
            </Box>

            <SetLabelDialog
                open={labelDialogOpen}
                session={selectedSession}
                onClose={() => setLabelDialogOpen(false)}
                onSaved={(updated) => { setSelectedSession(updated); loadInbox(); }}
            />

            <GenerateLinkDialog
                open={generatedLink !== null}
                url={generatedLink}
                onClose={() => setGeneratedLink(null)}
            />

            <LiveChatSettingsDialog
                open={settingsDialogOpen}
                onClose={() => setSettingsDialogOpen(false)}
                onChanged={loadBrands}
            />
        </Box>
    );
}
